/**
 * Patient Profile + Patient Dashboard wrapper for <FitQuizProjection />.
 *
 * Pulls the logged-in patient's most-recent Fit-Quiz submission via
 * `/wellness/fit-quiz/me`, fetches their actual weekly weigh-ins, and
 * renders the projection chart with the actual-weight overlay. Returns
 * `null` if the patient hasn't taken the quiz (so the card doesn't take
 * up dashboard real-estate for non-quiz patients).
 */
import { useEffect, useState } from "react";
import API from "@/lib/api";
import FitQuizProjection from "@/components/FitQuizProjection";
import { Sparkles } from "lucide-react";

export default function ProfileFitQuizCard() {
  const [submission, setSubmission] = useState(null);
  const [actuals, setActuals] = useState([]);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    let cancelled = false;
    Promise.all([
      API.get("/wellness/fit-quiz/me").catch(() => ({ data: { submission: null } })),
      API.get("/wellness/weight/series?limit=14").catch(() => ({ data: { series: [] } })),
    ]).then(([fq, ws]) => {
      if (cancelled) return;
      setSubmission(fq.data?.submission || null);
      setActuals(Array.isArray(ws.data?.series) ? ws.data.series : []);
    }).finally(() => { if (!cancelled) setLoaded(true); });
    return () => { cancelled = true; };
  }, []);

  if (!loaded || !submission) return null;

  const plan = (submission.recommended_plan || "").split("-");
  const drug = plan[0];
  const dose = plan[1];
  const startWeight = parseFloat(submission.weight_lb || 0);
  if (!drug || !dose || !startWeight) return null;

  // Map each actual weigh-in onto the projected curve's week-axis using
  // the quiz date as week-0 anchor.
  const quizDate = submission.created_at ? new Date(submission.created_at) : null;
  const actualPoints = actuals
    .filter((p) => p?.logged_at && p?.weight_lbs)
    .map((p) => {
      const wks = quizDate ? Math.max(0, Math.round((new Date(p.logged_at) - quizDate) / (7 * 86400000))) : null;
      return wks !== null && wks <= 13 ? { week: wks, weight: p.weight_lbs } : null;
    })
    .filter(Boolean)
    .sort((a, b) => a.week - b.week);

  return (
    <section
      className="bg-white rounded-2xl border border-purple-100 shadow-[0_8px_30px_rgb(0,0,0,0.04)] p-5 sm:p-6"
      data-testid="profile-fit-quiz-card"
    >
      <div className="flex items-center gap-2 mb-3">
        <div className="w-8 h-8 rounded-lg bg-purple-100 flex items-center justify-center">
          <Sparkles className="w-4 h-4 text-purple-700" />
        </div>
        <div>
          <h3 className="text-sm font-bold text-slate-900">Your 90-day projection</h3>
          <p className="text-xs text-slate-500">From the Fit Quiz you took on {new Date(submission.created_at).toLocaleDateString()}.</p>
        </div>
      </div>
      <FitQuizProjection
        startWeight={startWeight}
        drug={drug}
        dose={dose}
        actualPoints={actualPoints}
      />
      {actualPoints.length > 0 && (
        <p className="text-[11px] text-emerald-700 mt-2">
          {actualPoints.length} of your weekly weigh-in{actualPoints.length === 1 ? "" : "s"} plotted on top of the projection.
        </p>
      )}
    </section>
  );
}
